/*globals login*/
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useDimensions } from "common/hooks";
import BoxPlot from "common/BoxPlot";
import ThresholdModal from "./ThresholdModal";

const Wrapper = styled.div`
  width: 100%;
  margin: 0 10px 20px;
  position: relative;
`;

const Title = styled.div`
  width: 100%;
  h5 {
    margin: 0;
    cursor: pointer;
  }
`;

const WrapperBoxPlot = styled.div`
  width: 100%;
  height: 155px;
  position: relative;
`;

const WrapperThreshold = styled.div`
  background: transparent;
  align-items: center;
  display: flex;
  margin-top: 20px;
`;

const LabelClick = styled.div`
  cursor: pointer;
  border-radius: 6px;
  background-color: var(--Signal-Blue-Light);
  color: #37abff;
  font-size: 12px;
  line-height: 115%;
  letter-spacing: 0.24px;
  padding: 4px 2px 2px 2px;
  font-family: "AllianceNo2-Light";
  margin-left: 5px;
`;

const MARGIN = { top: 10, right: 10, bottom: 10, left: 20 };

const getMetricsAttributes = (login) => {
  return {
    "MEAN-Mbps": {
      field: "MEAN-Mbps",
      label: "MEAN-Mbps",
      units: "Mbps",
      legend: "Mean speed last 24h",
      tooltip:
        "Mean speed in Mbps during the previous 24 hours or since last subs-id/policy change",
      unit: "%",
    },
    "SUBS-ACTIVE": {
      field: "SUBS-ACTIVE",
      label: "SUBS-ACTIVE",
      inverted: true,
      units: "active subscribers",
      legend: "active subscribers",
      tooltip: "Active subscribers",
      unit: "%",
    },
    "RTT-ms": {
      field: "RTT-ms",
      label: "RTT-ms",
      inverted: true,
      units: "ms",
      legend: login.isPlatform("cambium")
        ? "Latency from QoE"
        : "Latency from bqn",
      tooltip:
        "Moving average of latency, in milliseconds. If downlink, latency from this node to subs, if uplink, latency from this node to Internet server",
      labelThreshold: "ms",
    },
    RTX: {
      field: "RTX",
      label: "RTX",
      inverted: true,
      units: "% retransmisions",
      legend: "TCP packet retransmission",
      tooltip:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
      percentage: true,
      labelThreshold: "%",
      unit: "%",
    },
    CONGEST: {
      field: "CONGEST",
      label: "CONGESTION",
      tooltip: "Moving average of percentage of traffic suffering congestion",
      inverted: true,
      units: "% of traffic",
      legend: "% traffic under congestion",
      percentage: true,
      labelThreshold: "%",
      unit: "%",
    },
  };
};

const Threshold = ({ metric, threshold, labelUnit, setDisplayModal }) => {
  if (!threshold) {
    return null;
  }
  const label = `> ${threshold}${labelUnit ? labelUnit : ""}`;
  return (
    <WrapperThreshold>
      <div>{"Warn if"}</div>
      <LabelClick onClick={() => setDisplayModal(true)}>{label}</LabelClick>
    </WrapperThreshold>
  );
};

const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 100%;
  min-height: ${(props) => props.minHeight}px;
  width: ${(props) => (props.width ? props.width + "px" : "auto")};
`;

export const NoEnoughtData = ({ minHeight, width }) => (
  <CenteredMessage minHeight={minHeight} width={width}>
    Not enough data.
  </CenteredMessage>
);

const SubscriberGroupBoxPlot = ({ metric, data, threshold, updateThresholds, colorize }) => {
  const [displayModal, setDisplayModal] = useState(false);
  const boxplotRef = useRef(null);
  const { width, height } = useDimensions(boxplotRef);
  const metricsAttributes = getMetricsAttributes(login);
  const {
    field,
    label,
    tooltip,
    inverted,
    units,
    legend,
    percentage,
    labelThreshold,
    unit,
  } = metricsAttributes[metric];
  return (
    <Wrapper>
      <Title>
        <h5 title={tooltip}>{label}</h5>
        <span>{legend}</span>
      </Title>
      {data.length === 0 ? (
        <NoEnoughtData minHeight={55} width={width} />
      ) : null}
      <WrapperBoxPlot ref={boxplotRef}>
        <BoxPlot
          data={data}
          width={width}
          height={height}
          margin={MARGIN}
          units={units}
          colorize={colorize}
          threshold={metric !== "MEAN-Mbps" ? threshold: undefined}
          inverted={inverted}
        />
      </WrapperBoxPlot>
      {metric !== "MEAN-Mbps" ? (
        <Threshold
          metric={metric}
          threshold={threshold}
          labelUnit={labelThreshold}
          setDisplayModal={setDisplayModal}
        />
      ) : null}
      {displayModal ? (
        <ThresholdModal
          setDisplayModal={setDisplayModal}
          metric={field}
          unit={unit}
          label={label}
          threshold={threshold}
          updateThresholds={updateThresholds}
        />
      ) : null}
    </Wrapper>
  );
};

export default SubscriberGroupBoxPlot;
